import React from 'react';
import theme from './BuyerOptionCard.module.scss';
import { ConfiguratorOptionDto } from '@/services/configurator';
import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { SimpleTooltip, Button, ButtonSize } from '@ygtechnologies/rubus-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOptionSelection } from './useOptionSelection';
import { QuantityStepper } from './QuantityStepper';

export interface OptionSelectButtonProps {
    option: ConfiguratorOptionDto;
    size?: ButtonSize;
    /** Called after a direct selection (not when the click opens the dependency wizard). */
    onSelected?: () => void;
}

export const OptionSelectButton: React.FC<OptionSelectButtonProps> = ({ option, size, onSelected }) => {
    const {
        selected,
        isSelecting,
        disabled,
        triggersWizard,
        tooltip,
        select,
        wizard,
        quantityEditable,
        currentQuantity,
        quantityDisabled,
    } = useOptionSelection(option);

    const handleSelect = () => {
        select();
        // A wizard click doesn't complete a selection yet, so leave the caller (e.g. the modal) open.
        if (!triggersWizard) onSelected?.();
    };

    if (selected) {
        return (
            <div className={theme.selectedSlot}>
                <span className={theme.selectedIndicator}>
                    <FontAwesomeIcon icon={faCheck} />
                    <Trans>Option selected</Trans>
                </span>
                {quantityEditable && (
                    <QuantityStepper
                        value={currentQuantity}
                        disabled={quantityDisabled}
                        onChange={(quantity) => select(quantity)}
                    />
                )}
            </div>
        );
    }

    // The normal, directly-selectable case uses the brand "primary" fill; every gated case
    // (disabled or dependency-wizard) keeps the neutral "brand" variant.
    const button = (
        <Button
            variant={!disabled && !triggersWizard ? 'primary' : 'brand'}
            disabled={disabled}
            onClick={disabled ? undefined : handleSelect}
            loading={isSelecting && !disabled}
            size={size}>
            <Trans>Select option</Trans>
        </Button>
    );

    return (
        <>
            {tooltip ? <SimpleTooltip content={tooltip}>{button}</SimpleTooltip> : button}
            {wizard}
        </>
    );
};
